import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { openPdfResponse } from "../../utils/openPdfInApp";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getAllCheckIn, printDcCheckin } from "../../services/CheckInServices";
import { getListViewSettings } from "../../services/SettingsServices";
import { buildCheckInQueryParams } from "../../utils/listViewDateRanges";
import { displayError } from "../../helpers";
import { mapCheckInListRow } from "./checkInListUtils";

function CheckInTable() {
    const InitialData = {
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);

    const columns = useMemo(
        () => [
            { fieldName: "plant", headerName: 'Plant', size: 80 },
            { fieldName: "checkout_no", headerName: 'CheckOut No.', size: 110 },
            { fieldName: "delivery_challan_no", headerName: 'DC Nos', size: 90 },
            { fieldName: "delivery_challan_date", headerName: 'DC Date', size: 90 },
            { fieldName: "order_no", headerName: 'SO Nos', size: 90 },
            { fieldName: 'customer_name', headerName: 'Billing Name', size: 160 },
            { fieldName: "consignee_name", headerName: 'Site Name', size: 160 },
            { fieldName: "grade", headerName: 'Grade', size: 90 },
            { fieldName: "dc_qty", headerName: 'DC Qty', size: 75, align: 'right' },
            { fieldName: "accepted_qty", headerName: 'Accepted Qty', size: 100, align: 'right' },
            { fieldName: "customer_status", headerName: 'Customer Status', size: 120 },
            { fieldName: "cus_dc_status", headerName: 'DC Status', size: 100 },
            { fieldName: "vehicle_no", headerName: 'Vehicle No', size: 110 },
            { fieldName: 'driver_name', headerName: 'Drivers Name', size: 120 },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const loadCheckInList = useCallback((config) => {
        if (!config) return;
        setIsLoading(true);
        getAllCheckIn(cookies, buildCheckInQueryParams(config))
            .then((checkinList) => {
                if (!checkinList?.checkin_list) {
                    throw new Error('Check-in list was not returned by the server.');
                }
                const tableData = checkinList.checkin_list.map(mapCheckInListRow);
                setData({ records: tableData });
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load check-in list');
            });
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadCheckInList(config);
    }, [loadCheckInList]);

    useEffect(() => {
        getListViewSettings(cookies, 'check_in')
            .then((response) => {
                const config = response.config || {};
                loadCheckInList(config);
            })
            .catch((error) => {
                setIsLoading(false);
                displayError(error.response?.data ?? error.message, 'Failed to load check-in settings');
            });
    }, [cookies, loadCheckInList]);

    const onEditCheckIn = (checkinId) => {
        navigate(`/CheckInEdit/${checkinId}/`);
    };

    const onPrintDeliveryChallan = (checkinId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printDcCheckin(cookies, checkinId)
            .then((response) => {
                openPdfResponse(response, {
                    title: 'Check In DC',
                    downloadFileName: 'checkin_dc.pdf',
                });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Print Failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(c => c.headerName),
            data.records.map((checkin) => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of CheckIn',
            columns
                .filter(col => col.headerName !== 'ROW_ACTIONS')
                .map(col => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((checkin) => ({
                plant: checkin.plant,
                checkout_no: checkin.checkout_no,
                delivery_challan_no: checkin.delivery_challan_no,
                delivery_challan_date: checkin.delivery_challan_date,
                order_no: checkin.order_no,
                customer_name: checkin.customer_name,
                consignee_name: checkin.consignee_name,
                grade: checkin.grade,
                dc_qty: checkin.dc_qty,
                accepted_qty: checkin.accepted_qty,
                customer_status: checkin.customer_status,
                cus_dc_status: checkin.cus_dc_status,
                vehicle_no: checkin.vehicle_no,
                driver_name: checkin.driver_name,
            })),
            'CheckInList.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="GATE"
                    title="List of Check In"
                    viewKey="check_in"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/CheckIn"
                    addNewLabel="New Check In"
                />
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of CheckIn'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditCheckIn}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintDeliveryChallan}
                            hidePrint={false}
                            compactColumns={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CheckInTable;
